/**
 * @fileoverview A base de conhecimento completa, para ver e ajustar: o que a
 * IA usa para responder, as páginas lidas do site, e salvar (o agente ganha
 * uma versão nova). Aberta por "Completar a base", já seleciona o trecho que
 * faltou (ou cria a seção no fim), para a pessoa só escrever.
 */

import React, { useEffect, useRef, useState } from "react";
import { Dialog, IconButton } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { X } from "lucide-react";

import { i18n } from "../../translate/i18n";
import { useAuthFormStyles } from "../AuthLayout/formStyles";
import { errorText } from "../AuthLayout/authHelpers";
import { useAiSetupStyles } from "./styles";

// As seções começam e terminam nos títulos (##). Um negrito sozinho na linha só
// vale como o título da seção procurada; dentro dela ("**Sábado:**") não a encerra.
const HEADING = /^\s*#{1,6}\s/;
const BOLD_LINE = /^\s*\*\*[^*]+\*\*:?\s*$/;

const KnowledgeDialog = ({ open, onClose, knowledge, pages, focusSection, onSave }) => {
  const classes = useAiSetupStyles();
  const form = useAuthFormStyles();
  const areaRef = useRef(null);
  const [text, setText] = useState(knowledge || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setText(knowledge || "");
      setError("");
    }
  }, [open, knowledge]);

  // O trecho da seção que faltou, selecionado (a pessoa escreve por cima do "Não informado").
  const selectSection = () => {
    const area = areaRef.current;
    if (!area || !focusSection) return;
    const value = area.value;
    const lines = value.split("\n");
    let offset = 0;
    let start = -1;
    let end = value.length;
    for (const line of lines) {
      if (start < 0 && (HEADING.test(line) || BOLD_LINE.test(line)) && line.toLowerCase().includes(focusSection.toLowerCase())) start = offset + line.length + 1;
      else if (start >= 0 && HEADING.test(line)) {
        end = Math.max(start, offset - 1);
        break;
      }
      offset += line.length + 1;
    }
    if (start < 0) {
      const added = `${value.trimEnd()}\n\n## ${focusSection}\n`;
      setText(added);
      requestAnimationFrame(() => {
        area.focus();
        area.setSelectionRange(added.length, added.length);
        area.scrollTop = area.scrollHeight;
      });
      return;
    }
    area.focus();
    area.setSelectionRange(start, Math.max(start, end));
    area.scrollTop = Math.max(0, (value.slice(0, start).split("\n").length - 2) * 21);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await onSave(text.trim());
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      PaperProps={{ className: classes.dialogPaper }}
      aria-labelledby="ai-knowledge-title"
      TransitionProps={{ onEntered: selectSection }}
    >
      <div className={classes.dialogHead}>
        <div>
          <h2 id="ai-knowledge-title" className={classes.dialogTitle}>{i18n.t("aiSetup.knowledge.title")}</h2>
          <p className={classes.dialogText}>{i18n.t("aiSetup.knowledge.subtitle")}</p>
        </div>
        <IconButton size="small" onClick={onClose} className={classes.dialogClose} aria-label={i18n.t("aiSetup.close")} disabled={saving}>
          <X size={18} />
        </IconButton>
      </div>
      <div className={classes.dialogBody}>
        <textarea
          ref={areaRef}
          className={`${form.input} ${classes.knowledgeArea}`}
          value={text}
          onChange={(event) => setText(event.target.value)}
          maxLength={14000}
          aria-label={i18n.t("aiSetup.knowledge.title")}
        />
        <span className={classes.counter}>{i18n.t("aiSetup.knowledge.count", { count: text.length })}</span>
        {error && <div className={`${form.notice} ${form.noticeBad}`} role="alert">{error}</div>}
        {pages?.length > 0 && (
          <div className={classes.pages}>
            <p className={classes.pagesTitle}>{i18n.t("aiSetup.knowledge.pages")}</p>
            {pages.map((page) => (
              <a key={page} href={page} target="_blank" rel="noopener noreferrer" className={classes.pageLink}>{page}</a>
            ))}
          </div>
        )}
      </div>
      <div className={classes.dialogFoot}>
        <button type="button" className={`${form.secondary} ${classes.secondaryInline}`} onClick={onClose} disabled={saving}>
          {i18n.t("aiSetup.knowledge.cancel")}
        </button>
        <button type="button" className={`${form.button} ${classes.primaryInline}`} onClick={save} disabled={saving || text.trim() === (knowledge || "").trim()}>
          {saving && <CircularProgress size={18} className={form.spinner} />}
          {i18n.t("aiSetup.knowledge.save")}
        </button>
      </div>
    </Dialog>
  );
};

export default KnowledgeDialog;
